import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Icon from './Icon';

// Boldranks müşterilerinin yaygın olduğu marketplaceler.
// Daha fazlası gerekirse buraya ekle (örn: A21TJRUUN4KGV=IN, A1VC38T7YXB528=JP).
export const MARKETPLACES = [
  { id: 'ATVPDKIKX0DER',  label: 'US', name: 'Amerika' },
  { id: 'A2EUQ1WTGCTBG2', label: 'CA', name: 'Kanada' },
  { id: 'A1AM78C64UM0Y8', label: 'MX', name: 'Meksika' },
  { id: 'A1F83G8C2ARO7P', label: 'UK', name: 'Birleşik Krallık' },
  { id: 'A1PA6795UKMFR9', label: 'DE', name: 'Almanya' },
];

// Amazon SP-API uygulaması (solution/application) id'si — public consent URL'inde görünür, gizli değil.
// Gerekirse REACT_APP_SPAPI_APP_ID env ile override edilir.
const SPAPI_APP_ID = process.env.REACT_APP_SPAPI_APP_ID || 'amzn1.sp.solution.13a08536-b2b5-40c0-b22d-d6226975d475';
// state backend'den alınır: imzalı ve 14 gün geçerli (hesap numarası tek başına artık kabul edilmez)
const authorizeLinkFor = (state) =>
  `https://sellercentral.amazon.com/apps/authorize/consent?application_id=${SPAPI_APP_ID}&state=${encodeURIComponent(state)}&version=beta`;

// AccountDetail'in Genel sekmesi altında gösterilen panel.
// hasToken: backend'den gelen boolean (token plain text dönmüyor).
// marketplaceIds: JSONB array (örn: ["ATVPDKIKX0DER"]).
// isAdmin: true ise düzenleme açık; değilse salt-okunur.
export default function SpApiSection({ accountId, hasToken, marketplaceIds, isAdmin, onChange }) {
  const [editing, setEditing] = useState(false);
  const [tokenDraft, setTokenDraft] = useState('');
  const [marketsDraft, setMarketsDraft] = useState(marketplaceIds || []);
  const [saving, setSaving] = useState(false);

  // Parent fetchAccount sonrası marketplaceIds güncellenince draft'ı senkronla
  useEffect(() => { setMarketsDraft(marketplaceIds || []); }, [marketplaceIds]);

  const startEdit = () => {
    setTokenDraft('');
    setMarketsDraft(marketplaceIds || []);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setTokenDraft('');
    setMarketsDraft(marketplaceIds || []);
  };

  const toggleMarket = (id) => {
    if (marketsDraft.includes(id)) {
      if (marketsDraft.length === 1) return; // en az 1 seçili kalmalı
      setMarketsDraft(marketsDraft.filter(m => m !== id));
    } else {
      setMarketsDraft([...marketsDraft, id]);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { marketplace_ids: marketsDraft };
      // Token sadece girildiyse gönder (boş = mevcut korunur)
      if (tokenDraft.trim() !== '') body.sp_api_refresh_token = tokenDraft.trim();
      await api.put(`/accounts/${accountId}/sp-api-credentials`, body);
      setEditing(false);
      setTokenDraft('');
      if (onChange) onChange();
    } catch (err) {
      alert('Kaydedilemedi: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const clearToken = async () => {
    if (!window.confirm('SP-API refresh token silinecek. Ciro çekme akışı bu hesap için duracak. Devam edilsin mi?')) return;
    setSaving(true);
    try {
      await api.put(`/accounts/${accountId}/sp-api-credentials`, { sp_api_refresh_token: null });
      if (onChange) onChange();
    } catch (err) {
      alert('Token silinemedi: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const ids = marketplaceIds || [];
  const [showLink, setShowLink] = useState(false);
  const [copied, setCopied] = useState(false);
  const [authState, setAuthState] = useState(null); // { state, expires_at }
  useEffect(() => {
    if (!showLink || authState) return;
    api.get(`/accounts/${accountId}/sp-api-state`).then(r => setAuthState(r.data)).catch(() => {});
  }, [showLink, authState, accountId]);
  const authorizeLink = authState ? authorizeLinkFor(authState.state) : '';
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(authorizeLink); } catch (e) { /* clipboard yoksa input'tan kopyalanır */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div style={{
      marginTop: 24, padding: 16,
      background: '#0A0B0D', border: '1px solid #17191C', borderRadius: 8,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ color: '#C9C7BE', fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, margin: 0 }}>
          Amazon SP-API Bağlantısı
        </h3>
        <span className="badge" style={{
          background: hasToken ? '#34D39922' : '#9A9A9222',
          color: hasToken ? '#34D399' : '#9A9A92',
        }}>
          {hasToken ? '✓ Bağlı' : '⊘ Bağlı değil'}
        </span>
      </div>

      {!editing ? (
        <>
          <div style={{ display: 'flex', gap: 24, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ color: '#9A9A92', fontSize: 12, marginBottom: 4 }}>Refresh Token</div>
              <div style={{ color: '#F2EFE6', fontSize: 14, fontFamily: 'monospace' }}>
                {hasToken ? '••••••••••••' : <span style={{ color: '#9A9A92' }}>Token girilmemiş</span>}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ color: '#9A9A92', fontSize: 12, marginBottom: 4 }}>Marketplaceler</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {ids.length === 0
                  ? <span style={{ color: '#9A9A92', fontSize: 13 }}>—</span>
                  : ids.map(id => {
                      const m = MARKETPLACES.find(mp => mp.id === id);
                      return (
                        <span key={id} className="badge" style={{
                          background: '#D4FF3A22', color: '#D4FF3A', fontSize: 12,
                        }}>
                          {m ? `${m.label} · ${m.name}` : id}
                        </span>
                      );
                    })}
              </div>
            </div>
          </div>

          {isAdmin ? (
            <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn--secondary" onClick={startEdit}>
                {hasToken ? '✎ Düzenle' : '+ Token Ekle'}
              </button>
              <button className="btn btn--secondary" onClick={() => setShowLink(s => !s)}>
                <Icon name="link" size={14} /> Yetkilendirme Linki
              </button>
              {hasToken && (
                <button
                  className="btn btn--secondary"
                  onClick={clearToken}
                  disabled={saving}
                  style={{ color: '#FF6B5B' }}
                >
                  <Icon name="trash" size={14} /> Tokenı Sil
                </button>
              )}
            </div>
            {showLink && (
              <div style={{ marginTop: 12, padding: 12, background: '#0E0F11', border: '1px solid #2A2D32', borderRadius: 8 }}>
                <div style={{ color: '#9A9A92', fontSize: 12, marginBottom: 6 }}>
                  Müşteriye gönderilecek yetkilendirme linki (Seller Central'da onaylar → token otomatik kaydolur).
                  {authState && <> Link {new Date(authState.expires_at).toLocaleDateString('tr-TR')} tarihine kadar geçerli.</>}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    readOnly
                    value={authorizeLink || 'Link hazırlanıyor…'}
                    onFocus={(e) => e.target.select()}
                    style={{ flex: 1, background: '#0A0B0D', border: '1px solid #2A2D32', borderRadius: 6, color: '#C9C7BE', fontSize: 12, padding: '8px 10px', fontFamily: 'monospace' }}
                  />
                  <button className="btn btn--secondary" onClick={copyLink} style={{ whiteSpace: 'nowrap' }}>
                    {copied ? '✓ Kopyalandı' : <><Icon name="copy" size={14} /> Kopyala</>}
                  </button>
                  <a className="btn btn--secondary" href={authorizeLink} target="_blank" rel="noopener noreferrer" style={{ whiteSpace: 'nowrap', textDecoration: 'none' }}>
                    ↗ Aç
                  </a>
                </div>
              </div>
            )}
            </>
          ) : (
            <p style={{ color: '#9A9A92', fontSize: 11, margin: 0 }}>
              <Icon name="lock" size={14} /> SP-API credentials sadece admin tarafından düzenlenebilir.
            </p>
          )}
        </>
      ) : (
        <>
          <div className="form-group">
            <label className="form-label">
              Refresh Token {hasToken && (
                <span style={{ color: '#9A9A92', fontSize: 11 }}>(boş bırakılırsa mevcut korunur)</span>
              )}
            </label>
            <input
              type="password"
              className="form-input"
              value={tokenDraft}
              onChange={(e) => setTokenDraft(e.target.value)}
              placeholder={hasToken ? 'Yeniden girmek için yazın' : 'Atza|... veya yeni LWA refresh tokenı'}
              autoComplete="off"
              style={{ fontFamily: 'monospace' }}
            />
            <p style={{ color: '#9A9A92', fontSize: 11, marginTop: 4 }}>
              Bu hesabın Seller Central → Apps &amp; Services → Authorize akışından alınan tokenıdır.
              Tek bir hesap için tek token; revoke edilmediği sürece süresizdir.
            </p>
          </div>

          <div className="form-group">
            <label className="form-label">Marketplaceler</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {MARKETPLACES.map(m => {
                const selected = marketsDraft.includes(m.id);
                return (
                  <label
                    key={m.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '6px 12px',
                      background: selected ? '#D4FF3A22' : '#17191C',
                      border: selected ? '1px solid #D4FF3A' : '1px solid transparent',
                      borderRadius: 6, cursor: 'pointer',
                      fontSize: 13, color: selected ? '#D4FF3A' : '#C9C7BE',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleMarket(m.id)}
                      style={{ margin: 0 }}
                    />
                    {m.label} · {m.name}
                  </label>
                );
              })}
            </div>
            <p style={{ color: '#9A9A92', fontSize: 11, marginTop: 6 }}>
              En az 1 marketplace seçili olmalı. Her marketplace için ayrı API çağrısı yapılır, currency ayrı gelir.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn--primary" onClick={save} disabled={saving}>
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
            <button className="btn btn--secondary" onClick={cancel} disabled={saving}>
              İptal
            </button>
          </div>
        </>
      )}
    </div>
  );
}
